import * as React from "react";
import { useState, useContext, useEffect, useMemo } from "react";
import FormCheckLabel from "react-bootstrap/esm/FormCheckLabel";
import {
  Button,
  Form,
  FormCheck,
  InputGroup,
  ListGroup,
  Tab,
} from "react-bootstrap";
import _ from "lodash";
import isEqual from "lodash/isEqual";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CCOffcanvas from "./CCOffcanvas.tsx";
import { HttpContext } from "../../api-services/HttpService";
import PageContext from "./../sub-components/PageContext";
import { Favorites } from "./../main-components/FavoritesRecentRecordsTabs.tsx";
import ApiRequestButton from "../sub-components/ApiRequestButton.jsx";
import FavoriteLinks from "../../assets/favoritesLinks.json";
import "../../sass/_addFavorites.scss";
import infoIcon from "../../svgs/info.svg";
import PageTooltip from "../sub-components/PageTooltip.jsx";

type Props = {
  title?: string;
  show?: boolean;
  onCanvasClose: () => void;
  savedFavorites?: Favorites[];
  setSavedFavorites: () => void;
  reloadFavorites: boolean;
  setReloadFavorites: () => void;
  allFavLinks: [];
};

const AddFavoritesCanvas = ({
  title,
  show,
  onCanvasClose,
  savedFavorites,
  setSavedFavorites,
  reloadFavorites,
  setReloadFavorites,
  allFavLinks,
}: Props) => {
  const { POST } = useContext(HttpContext);
  const { snackbarAlert } = useContext(PageContext);
  const [message, setMessage] = useState("");
  const [savedLinks, setSavedLinks] = useState([]);
  const [updatedFavLink, setUpdatedFavLink] = useState([]);
  const [addDisabled, setAddDisabled] = useState(true);

  //fetch link value only from Favorites
  const fetchLinksFromFav = (favorites) => {
    let links = [];
    favorites.forEach((item) => {
      if (item?.link) {
        links.push(item.link);
      }
    });
    return links;
  };

  //fetch full favorite object from only link value
  const fetchFavFromLinks = (links) => {
    let tempFavs = [];
    links.forEach((item) => {
      let favorite = allFavLinks.find((fav) => fav.link === item);
      tempFavs.push(favorite);
    });
    setSavedFavorites(tempFavs);
  };

  //calling add favorites api for saving the selected favorites
  const addFavorites = () => {
    return new Promise((resolve, reject) => {
      let response;
      let params = {};
      let favorites = [];
      updatedFavLink.forEach((item) => {
        let favorite = {
          path: item,
        };
        favorites.push(favorite);
      });
      params = {
        favoriteRecords: favorites,
      };
      POST("/links/favorite", params)
        .then((res) => {
          // show success on snackbar
          setSavedLinks(updatedFavLink);
          fetchFavFromLinks(updatedFavLink);
          setReloadFavorites(() => !reloadFavorites);
          snackbarAlert(
            true,
            "Successfully updated favorite links.",
            "success"
          );
          onCanvasClose();
        })
        .then((res) => {
          // resolve on success
          resolve(response);
        })
        .catch((err) => {
          console.log(err);
          // show error on snackbar
          snackbarAlert(true, "Error updating favorite links.", "error");
          reject(err);
        });
    });
  };

  // check if the values changed when compared to the already saved favorites
  const checkValuesChanged = (event) => {
    const newFavorite = event.target.value;
    if (event?.target?.checked && updatedFavLink.length == 14) {
      event.target.checked = false;
      setMessage("Allowed max 14 links");
    } else if (updatedFavLink.length != 14 && event?.target?.checked) {
      setMessage("");
      setUpdatedFavLink((prevState) => [...prevState, newFavorite]);
    } else if (event?.target?.checked == false) {
      setMessage("");
      setUpdatedFavLink((prevItems) =>
        prevItems.filter((item) => item !== newFavorite)
      );
    }
  };

  //action when close canvas is triggered
  const onCanvasCloseAction = () => {
    setUpdatedFavLink(savedLinks);
    setMessage("");
    onCanvasClose();
  };

  //use effect invoked during component load to load saved and updated fav link
  useEffect(() => {
    let favs = fetchLinksFromFav(savedFavorites);
    setUpdatedFavLink(favs);
    setSavedLinks(favs);
  }, [savedFavorites]);

  //use effect invoked when favorites are selected or unselected
  useEffect(() => {
    _.isEqual(savedLinks.sort(), updatedFavLink.sort())
      ? setAddDisabled(true)
      : setAddDisabled(false);
  }, [updatedFavLink]);

  return (
    <CCOffcanvas
      title={title}
      show={show}
      onCanvasClose={onCanvasClose}
      bodyCssClassName="add-fav-offcanvas-body"
    >
      <div>
        <div>
          <Form key="addFavorites">
            {FavoriteLinks.data.map((link, index) => {
              return (
                <div className="fav-links-title" key={link.id + index}>
                  <div className="mb-1 mt-1">{link.name}</div>
                  {link.links.map((item, i) => (
                    <div
                      key={"div" + item.id}
                      className="fav-link-div"
                      style={{ display: "flex" }}
                    >
                      <FormCheck
                        key={item.id + i}
                        type="checkbox"
                        id={item.id}
                        name={item.name}
                        value={item.link}
                        defaultChecked={savedLinks?.some(
                          (fav) => fav === item.link
                        )}
                        onChange={(e) => {
                          e.stopPropagation();
                          checkValuesChanged(e);
                        }}
                      />
                      <FormCheckLabel htmlFor={item.id}>
                        <LeaderboardOutlinedIcon className="ms-2 me-2 mb-1 icon-style" />
                        {<span className="fav-link-span">{item.name}</span>}
                      </FormCheckLabel>
                    </div>
                  ))}
                </div>
              );
            })}
          </Form>
        </div>
        <br />

      </div>
        <div className="add-fav-button-div">
          <div className="add-fav-error-div">
            {message && (
              <span style={{ position: "relative", top: "12px" }}>
                {message}
              </span>
            )}
            <ApiRequestButton
              css=" ms-2 modal-button modal-spinner-action"
              tag="Add"
              disabled={addDisabled}
              onButtonClick={addFavorites}
            />
            <Button
              className=" ms-2 modal-button"
              onClick={onCanvasCloseAction}
            >
              Cancel
            </Button>
          </div>
        </div>
    </CCOffcanvas>
  );
};

export default AddFavoritesCanvas;
